import React, { createContext, useContext, type PropsWithChildren, useMemo } from 'react';
import { GrowthBookProvider as BaseGrowthBookProvider, GrowthBookPayload, TrackingCallback } from '@growthbook/growthbook-react';
import { GrowthBook } from '@growthbook/growthbook-react';
import configJSON from 'config.json';
import { ENVIRONMENT_PRODUCTION, getEnvironment, isEndToEndTest } from 'utils/environment';
import { AnalyticsController } from 'analytics/analytics-controller';
import Cookies from 'js-cookie';
import { AREA_PAGE_FOURTH_MAP_CARD_COOKIE_NAME, MAP_SINGLE_LISTING_PREVIEW_COOKIE_NAME } from 'constants/cookies';
import { AREA_PAGE_FOURTH_MAP_CARD_FLAG, MAP_SINGLE_LISTING_PREVIEW_FLAG } from 'constants/feature-flags';
import { isClientSide } from 'utils/host-config';
import { TenantId, AllowedExperimentKey, AllowedFeatureFlagKey } from '@tenants/types';
import { SSRExperimentResults, SSRExperimentMetadata, SSRFeatureFlagResults } from './growthbook-ssr-payload';

// Log GrowthBook status once at module load
if (isEndToEndTest) {
  console.warn('[GrowthBook:Provider] DISABLED - isEndToEndTest=true. Run with ENABLE_GROWTHBOOK=true to enable.');
}

interface ExperimentContextType {
  /** SSR experiment decisions (boolean per experiment) */
  ssrResults: SSRExperimentResults;
  /** SSR experiment metadata (piped to client for logging) */
  ssrMetadata: SSRExperimentMetadata;
  /** Check if user is in variation for a given experiment */
  isInVariation: (key: AllowedExperimentKey) => boolean;
}

interface FeatureFlagContextType {
  /** SSR feature flag decisions (boolean per flag) */
  ssrFeatureFlags: SSRFeatureFlagResults;
  /** Check if a feature flag is on (SSR-evaluated, no tenant gating) */
  isFeatureOn: (key: AllowedFeatureFlagKey) => boolean;
}

const ExperimentContext = createContext<ExperimentContextType | null>(null);
const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

export function useExperimentContext(): ExperimentContextType {
  const context = useContext(ExperimentContext);
  if (!context) {
    throw new Error('useExperimentContext must be used within GrowthBookProvider');
  }
  return context;
}

export function useFeatureFlagContext(): FeatureFlagContextType {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlagContext must be used within GrowthBookProvider');
  }
  return context;
}

type GrowthBookProviderProps = PropsWithChildren<{
  /** Device ID for anonymous user tracking. */
  deviceId: string;
  /** Optional logged in user identification */
  loggedInId?: string | number;
  /** Optional tenant ID for tenant-specific targeting */
  tenantId?: TenantId;
  /** Optional GrowthBook payload from server-side rendering for hydration */
  payload?: GrowthBookPayload;
  /** Pre-evaluated experiment results from SSR */
  ssrExperimentResults?: SSRExperimentResults;
  /** 
   * Experiment metadata from SSR (for client-side logging).
   * This "pipes" SSR experiment context to the client so all logs
   * appear in the browser console with complete information.
   */
  ssrExperimentMetadata?: SSRExperimentMetadata;
  /** Pre-evaluated feature flag results from SSR */
  ssrFeatureFlagResults?: SSRFeatureFlagResults;
}>;

/**
 * Tracking callback for GrowthBook experiment views.
 *
 * This function is called whenever a user is exposed to an experiment variation.
 * It logs the experiment details and tracks the event through the analytics system.
 *
 * @param experiment - The experiment object containing experiment metadata
 * @param result - The experiment result containing variation information
 */
const onExperimentViewed: TrackingCallback = (experiment, result) => {
  const experimentId = experiment.key;
  const variationId = result.key;

  console.debug('[GrowthBook:Provider] Viewed Experiment', { experimentId, variationId });

  AnalyticsController.trackGrowthbookEvent(experiment.key, result.variationId);
};

/**
 * A wrapper around the GrowthBook React provider for client-side feature flag management.
 *
 * This component initializes a GrowthBook instance with user attributes and feature definitions,
 * enabling client-side feature flag evaluation and experiment tracking. It supports both
 * server-side rendered payloads and client-side initialization.
 *
 * @param children React children components that will have access to GrowthBook context
 * @param user Optional user object containing user identification and attributes
 * @param deviceId Optional device ID for anonymous user tracking
 * @param payload Optional GrowthBook payload from server-side rendering for hydration
 *
 * @example
 * ```tsx
 * // Basic usage without SSR
 * <GrowthBookProvider loggedInId={user?.id} deviceId={deviceId}>
 *   <App />
 * </GrowthBookProvider>
 *
 * // With SSR payload
 * <GrowthBookProvider loggedInId={user?.id} deviceId={deviceId} payload={growthBookPayload}>
 *   <App />
 * </GrowthBookProvider>
 * ```
 */
export const GrowthBookProvider = ({ children, loggedInId, deviceId, tenantId, payload, ssrExperimentResults, ssrExperimentMetadata, ssrFeatureFlagResults }: GrowthBookProviderProps) => {
  // Create a singleton GrowthBook instance for the app
  const growthbook = useMemo(
    () => {
      const gb = new GrowthBook({
        apiHost: configJSON.growthbook.apiHost,
        clientKey: configJSON.growthbook.clientKey,
        enableDevMode: getEnvironment() !== ENVIRONMENT_PRODUCTION,
        trackingCallback: onExperimentViewed,
        subscribeToChanges: true,
        enabled: !isEndToEndTest,
        attributes: { 
          id: deviceId, 
          anonymous_id: deviceId, 
          loggedIn: loggedInId,
          tenantId: tenantId,
          environment: getEnvironment(),
        },
      }).initSync({
        payload,
      });

      if (isClientSide()) {
        // Set cookies based on feature flag evaluations
        const showMapCardExperimentView = gb.getFeatureValue(AREA_PAGE_FOURTH_MAP_CARD_FLAG, 0) == 1;
        Cookies.set(AREA_PAGE_FOURTH_MAP_CARD_COOKIE_NAME, showMapCardExperimentView.toString());

        // Set cookie for map single listing preview experiment
        const showMapSingleListingPreview = gb.getFeatureValue(MAP_SINGLE_LISTING_PREVIEW_FLAG, 0) == 1;
        Cookies.set(MAP_SINGLE_LISTING_PREVIEW_COOKIE_NAME, showMapSingleListingPreview.toString());
      }
      return gb;
    },
    [payload, deviceId, loggedInId, tenantId]
  );

  const experimentContext = useMemo(() => ({
    ssrResults: ssrExperimentResults ?? {} as SSRExperimentResults,
    ssrMetadata: ssrExperimentMetadata ?? {},
    isInVariation: (key: AllowedExperimentKey): boolean => ssrExperimentResults?.[key] ?? false,
  }), [ssrExperimentResults, ssrExperimentMetadata]);

  const featureFlagContext = useMemo(() => ({
    ssrFeatureFlags: ssrFeatureFlagResults ?? {} as SSRFeatureFlagResults,
    isFeatureOn: (key: AllowedFeatureFlagKey): boolean => ssrFeatureFlagResults?.[key] ?? false,
  }), [ssrFeatureFlagResults]);

  return (
    <BaseGrowthBookProvider growthbook={growthbook}>
      <ExperimentContext.Provider value={experimentContext}>
        <FeatureFlagContext.Provider value={featureFlagContext}>
          {children}
        </FeatureFlagContext.Provider>
      </ExperimentContext.Provider>
    </BaseGrowthBookProvider>
  );
};